import React, { useEffect, useRef, useState } from "react";
import { Send, X, MessageCircle, RotateCcw, Sparkles } from "lucide-react";
import { useLang } from "../i18n";
import { sendObiChat } from "../lib/api";
import { OBI } from "../assets/obi";

const newSessionId = () =>
  `obi_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;

const ObiChatWidget = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("obi_session_id") || newSessionId();
  });
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem("obi_messages");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("obi_session_id", sessionId);
  }, [sessionId]);

  useEffect(() => {
    localStorage.setItem("obi_messages", JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, thinking]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || thinking) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setThinking(true);
    try {
      const res = await sendObiChat(sessionId, msg, lang);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            lang === "it"
              ? "Bau! Connessione spezzata, riprova fra poco."
              : "Woof! Connection broke, try again soon.",
        },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId(newSessionId());
  };

  const quickPrompts = [t("chat.quick_1"), t("chat.quick_2"), t("chat.quick_3")];

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="open-obi-chat"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 group flex items-center gap-3 bg-pink-400 hover:bg-pink-300 border-4 border-slate-900 rounded-full pl-2 pr-5 py-2 cartoon-shadow hover:cartoon-shadow-lg transition-all"
          aria-label={t("chat.open")}
        >
          <span className="relative w-12 h-12 bg-yellow-300 border-4 border-slate-900 rounded-full overflow-hidden shrink-0">
            <img src={OBI.dice} alt="OBI" className="absolute inset-0 w-full h-full object-cover" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 border-2 border-slate-900 rounded-full" />
          </span>
          <span className="font-display font-bold text-slate-900">{t("chat.open")}</span>
        </button>
      )}

      {/* Chat drawer */}
      {open && (
        <div
          data-testid="obi-chat-drawer"
          className="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-5 sm:right-5 z-50 w-auto sm:w-[400px] max-h-[80vh] flex flex-col bg-amber-50 border-4 border-slate-900 rounded-3xl cartoon-shadow-lg overflow-hidden animate-fade-up"
        >
          <header className="flex items-center justify-between gap-3 p-4 bg-pink-300 border-b-4 border-slate-900">
            <div className="flex items-center gap-3 min-w-0">
              <span className="relative w-12 h-12 bg-yellow-300 border-4 border-slate-900 rounded-full overflow-hidden shrink-0">
                <img src={OBI.dice} alt="OBI" className="w-full h-full object-cover" />
              </span>
              <div className="min-w-0">
                <h3 className="font-display font-bold text-lg leading-tight truncate">
                  {t("chat.title")}
                </h3>
                <p className="text-xs text-slate-700 truncate">{t("chat.subtitle")}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="reset-obi-chat"
                onClick={resetChat}
                className="w-9 h-9 bg-white border-2 border-slate-900 rounded-full flex items-center justify-center hover:bg-yellow-200"
                title={t("chat.clear")}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                data-testid="close-obi-chat"
                onClick={() => setOpen(false)}
                className="w-9 h-9 bg-white border-2 border-slate-900 rounded-full flex items-center justify-center hover:bg-pink-100"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </header>

          <div
            ref={scrollRef}
            data-testid="obi-chat-messages"
            className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[280px]"
            style={{ backgroundImage: "radial-gradient(circle at 25px 25px, rgba(15,23,42,0.06) 2px, transparent 0)", backgroundSize: "40px 40px" }}
          >
            {/* Welcome */}
            <div className="flex gap-2">
              <span className="w-8 h-8 bg-sky-300 border-2 border-slate-900 rounded-full overflow-hidden shrink-0">
                <img src={OBI.dice} alt="" className="w-full h-full object-cover" />
              </span>
              <div className="bg-white border-2 border-slate-900 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%] text-sm leading-relaxed">
                {t("chat.welcome")}
              </div>
            </div>

            {messages.map((m, i) => (
              <div
                key={m.id || `${m.role}-${i}`}
                className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}
                data-testid={`chat-msg-${m.role}-${i}`}
              >
                {m.role === "assistant" && (
                  <span className="w-8 h-8 bg-sky-300 border-2 border-slate-900 rounded-full overflow-hidden shrink-0">
                    <img src={OBI.dice} alt="" className="w-full h-full object-cover" />
                  </span>
                )}
                <div
                  className={`border-2 border-slate-900 rounded-2xl px-3 py-2 max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-yellow-300 rounded-tr-sm"
                      : "bg-white rounded-tl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {thinking && (
              <div className="flex gap-2" data-testid="chat-thinking">
                <span className="w-8 h-8 bg-sky-300 border-2 border-slate-900 rounded-full overflow-hidden shrink-0">
                  <img src={OBI.dice} alt="" className="w-full h-full object-cover" />
                </span>
                <div className="bg-white border-2 border-slate-900 rounded-2xl rounded-tl-sm px-3 py-2 text-sm">
                  <span className="inline-flex items-center gap-1">
                    <Sparkles className="w-4 h-4 animate-pulse text-pink-500" />
                    <span className="text-slate-600 italic">{t("chat.thinking")}</span>
                  </span>
                </div>
              </div>
            )}

            {/* Quick prompts */}
            {messages.length === 0 && !thinking && (
              <div className="flex flex-wrap gap-2 pt-2">
                {quickPrompts.map((q, i) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    data-testid={`chat-quick-${i}`}
                    className="bg-white border-2 border-slate-900 rounded-full px-3 py-1 text-xs font-bold hover:bg-yellow-200 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 p-3 border-t-4 border-slate-900 bg-white"
          >
            <input
              ref={inputRef}
              data-testid="obi-chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder")}
              maxLength={1000}
              className="cartoon-input flex-1 py-2 text-sm"
              disabled={thinking}
            />
            <button
              data-testid="obi-chat-send"
              type="submit"
              disabled={thinking || !input.trim()}
              className="w-11 h-11 bg-sky-400 hover:bg-sky-300 border-4 border-slate-900 rounded-full flex items-center justify-center cartoon-shadow-sm disabled:opacity-50"
              aria-label={t("chat.send")}
            >
              <Send className="w-4 h-4" strokeWidth={3} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ObiChatWidget;
